import { useEffect, useState } from 'preact/hooks';
import { parseImportString } from '@ms-cloudpack/path-string-parsing';
import { useCloudpack } from '../CloudpackProvider/CloudpackProvider.js';
import { errorEntrySource } from '@ms-cloudpack/api-server/browser';

// Interface with reason as a record.
interface ReasonRecord extends PromiseRejectionEvent {
  readonly reason: Record<string, unknown>;
}

// Interface with message as a string.
interface ReasonString extends ReasonRecord {
  readonly reason: { message: string };
}

// Verify the reason is a record.
const reasonIsRecord = (event: PromiseRejectionEvent): event is ReasonRecord => {
  return typeof event.reason === 'object' && !Array.isArray(event.reason) && event.reason !== null;
};

// Verify the reason has a message which is a string.
const hasMessage = (event: PromiseRejectionEvent): event is ReasonString => {
  return reasonIsRecord(event) && typeof event.reason.message === 'string';
};

export type ErrorEvents = {
  unsupported: UnsupportedErrorEvent[];
};

export type UnsupportedErrorEvent = {
  /** Package name being imported */
  packageName: string;
  /** Path within the package being imported (`'.'` for root) */
  importPath: string;
  /**
   * URL that issued the bad request. This will be undefined if the browser didn't set it, or if it
   * was incorrectly set to the current page URL (most browsers except Firefox do this).
   */
  issuerUrl?: string;
  /** True if this is a valid import that can be handled by `addPackageOverride` */
  fixable?: boolean;
  /** True if the package exists in the resolve map (even if the import path wasn't valid) */
  isValidPackage?: boolean;
};

export const useErrorEvents = (): ErrorEvents => {
  const [errorEvents, setErrorEvents] = useState<ErrorEvents>({ unsupported: [] });
  const cloudpack = useCloudpack();
  // This should always be set by an inline script
  const pageErrors = window.__pageErrors;

  useEffect(() => {
    if (!pageErrors) {
      console.error('[Cloudpack] window.__pageErrors not found (unable to handle unsupported imports)');
      return;
    }

    const handleUnsupportedError = (message: string, issuerUrl?: string): void => {
      const errorRegex = /[Mm]odule specifier,? ['"](.*?)['"]/;
      const match = message.match(errorRegex);

      if (!match) {
        return;
      }

      const importString = parseImportString(match[1]);
      const { packageName, importPath } = importString;

      (async () => {
        if (issuerUrl && issuerUrl === location.href) {
          // Issuer URL is often incorrectly set to the current page URL, so just remove it
          issuerUrl = undefined;
        }

        // Check whether importPath is valid for this package.
        // Unfortunately, due to the lack of issuerUrl support in most browsers, this might not be
        // accurate if there are multiple versions of the same package (it will fall back to
        // whichever version is at the top level of the resolve map, which may not be the version
        // that's actually being imported in context).
        const { fixable } = await cloudpack.validatePackageOverride.query({
          packageName,
          importPath,
          issuerUrl,
        });

        // If importPath doesn't appear to be valid, attempt to check whether the package exists
        // elsewhere in the resolve map (to refine the error message). This check isn't perfect
        // since it assumes the path '.' exists for that package, and it's also possible that the
        // package is installed somewhere in the repo but hasn't ended up in the resolve map yet.
        const isValidPackage =
          fixable ||
          (importPath !== '.' &&
            (await cloudpack.validatePackageOverride.query({ packageName, importPath: '.', issuerUrl })).fixable);

        setErrorEvents((prev) => ({
          ...prev,
          unsupported: [...prev.unsupported, { packageName, importPath, issuerUrl, fixable, isValidPackage }],
        }));
      })().catch(() => {
        // no-op
      });
    };

    const subscription = cloudpack.onDataChanged.subscribe(errorEntrySource, {
      onData: (data) => {
        // should always be a string. empty means no more errors.
        if (data && typeof data === 'string') {
          console.error(data);
          handleUnsupportedError(data);
        }
      },
    });

    pageErrors.unregister();

    // Append unsupported paths to the list.
    window.addEventListener('error', (event: ErrorEvent) => {
      handleUnsupportedError(event.message, event.filename);
    });

    // Handle errors caught before rendering.
    for (const error of pageErrors.uncaughtErrors) {
      handleUnsupportedError(error.message, error.filename);
    }

    // Append unsupported paths to the list.
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      hasMessage(event) && handleUnsupportedError(event.reason.message);
    });

    // Handle rejections caught before rendering.
    for (const error of pageErrors.uncaughtRejections) {
      hasMessage(error) && handleUnsupportedError(error.reason.message);
    }

    return () => {
      subscription.unsubscribe();
    };
  }, [cloudpack, pageErrors]);

  return errorEvents;
};
